import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './SideBar.css';
import { appNavs, NavGroup } from '../../config';
import SideBarNav from './SidebarNav';
import SidebarNavToggle from './SidebarNavToggle';
import SidebarUserMenu from './SidebarUserMenu';
import ColorModeToggle from '../ColorModeToggle';
import { View, useBreakpointValue, Button, Flex } from '@aws-amplify/ui-react';
import { MdClose } from 'react-icons/md';
import { listListings } from '../../api/admin';

const SideBar = () => {
  const [expand, setExpand] = useState(false);
  const [hasDrift, setHasDrift] = useState(false);
  const location = useLocation();

  const isDesktop = useBreakpointValue({
    base: false,
    small: false,
    medium: false,
    large: true,
  }) as boolean;

  useEffect(() => {
    setExpand(false);
  }, [location]);

  // One-shot drift signal: soft fetch on mount; failures are silent.
  useEffect(() => {
    let cancelled = false;
    listListings()
      .then(listings => {
        if (cancelled) return;
        const drift = listings.some(
          l =>
            l.status === 'publish_failed' ||
            (l.externalVariantDrift &&
              (l.externalVariantDrift.added.length > 0 ||
                l.externalVariantDrift.removed.length > 0))
        );
        setHasDrift(drift);
      })
      .catch(() => {
        // Soft signal — leave hasDrift false on error.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const driftDot = <span className="sidebar-drift-dot" aria-label="Needs attention" />;

  const navsWithBadges: NavGroup[] = appNavs.map(group => ({
    ...group,
    items: group.items.map(item =>
      item.eventKey === 'listings' && hasDrift ? { ...item, badge: driftDot } : item
    ),
  }));

  return (
    <>
      <View className="btn-sidebar-nav">
        <SidebarNavToggle expand={expand} onChange={() => setExpand(!expand)} />
      </View>
      <View
        backgroundColor={isDesktop ? 'background.tertiary' : 'background.primary'}
        className={'sidebar ' + (expand ? 'visible' : '')}
        height="100%"
      >
        {!isDesktop && expand && (
          // Floats top-right over the drawer — absolute-positioned so it
          // doesn't take up vertical flex space and push the logo + nav down.
          <Button
            variation="link"
            onClick={() => setExpand(false)}
            aria-label="Close navigation"
            data-testid="sidebar-close"
            className="sidebar-close"
            color="font.primary"
          >
            <MdClose size={24} />
          </Button>
        )}
        <Flex direction="column" justifyContent="space-between" className="sidebar-inner">
          <SideBarNav navs={navsWithBadges} />
          <View className="sidebar-footer">
            <Flex direction="row" alignItems="center" gap="xs" justifyContent="flex-start">
              <SidebarUserMenu />
              <ColorModeToggle />
            </Flex>
          </View>
        </Flex>
      </View>
    </>
  );
};

export default SideBar;
